import React from "react";
import {
  AbsoluteFill,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { COLORS, FONTS, SPRING } from "./constants";
import { GridBackground } from "./components/GridBackground";

interface GraphNode {
  id: string;
  label: string;
  type: string;
  x: number;
  y: number;
  color: string;
  delay: number;
}

interface GraphEdge {
  from: string;
  to: string;
  label: string;
  delay: number;
}

const NODES: GraphNode[] = [
  { id: "auth", label: "Auth System", type: "PROJECT", x: 960, y: 350, color: COLORS.accentBlue, delay: 15 },
  { id: "supabase", label: "Supabase", type: "TOOL", x: 650, y: 280, color: COLORS.accentTeal, delay: 30 },
  { id: "jwt", label: "JWT", type: "CONCEPT", x: 1270, y: 280, color: COLORS.accentPurple, delay: 40 },
  { id: "rls", label: "RLS Policies", type: "CONCEPT", x: 500, y: 480, color: COLORS.accentGreen, delay: 50 },
  { id: "openrouter", label: "OpenRouter", type: "TOOL", x: 1420, y: 480, color: COLORS.accentOrange, delay: 55 },
  { id: "rag", label: "RAG Pipeline", type: "PROJECT", x: 960, y: 600, color: COLORS.accentPink, delay: 60 },
  { id: "pgvector", label: "pgvector", type: "TOOL", x: 700, y: 700, color: COLORS.accentTeal, delay: 70 },
  { id: "embedding", label: "Embeddings", type: "CONCEPT", x: 1220, y: 700, color: COLORS.accentYellow, delay: 75 },
  { id: "nextjs", label: "Next.js 15", type: "TOOL", x: 350, y: 350, color: COLORS.layerClient, delay: 35 },
  { id: "mcp", label: "MCP Server", type: "PROJECT", x: 1550, y: 350, color: COLORS.accentPurple, delay: 65 },
];

const EDGES: GraphEdge[] = [
  { from: "auth", to: "supabase", label: "USES", delay: 45 },
  { from: "auth", to: "jwt", label: "IMPLEMENTS", delay: 50 },
  { from: "supabase", to: "rls", label: "ENFORCES", delay: 60 },
  { from: "rag", to: "pgvector", label: "QUERIES", delay: 80 },
  { from: "rag", to: "embedding", label: "GENERATES", delay: 85 },
  { from: "rag", to: "openrouter", label: "CALLS", delay: 75 },
  { from: "auth", to: "rag", label: "PROTECTS", delay: 70 },
  { from: "nextjs", to: "auth", label: "HOSTS", delay: 55 },
  { from: "mcp", to: "rag", label: "DELEGATES", delay: 80 },
  { from: "mcp", to: "jwt", label: "VALIDATES", delay: 78 },
];

const NODE_RADIUS = 42;

export const Scene6KnowledgeGraph: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const titleAppear = spring({ frame, fps, config: SPRING.snappy });

  const exitOpacity = interpolate(frame, [210, 240], [1, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  // Find node by id
  const nodeMap = new Map(NODES.map((n) => [n.id, n]));

  return (
    <AbsoluteFill style={{ opacity: exitOpacity }}>
      <GridBackground opacity={0.05} />

      {/* Title */}
      <div
        style={{
          position: "absolute",
          top: 50,
          left: "50%",
          transform: "translate(-50%, 0)",
          textAlign: "center",
          opacity: interpolate(titleAppear, [0, 1], [0, 1]),
        }}
      >
        <div
          style={{
            fontSize: 42,
            fontFamily: FONTS.heading,
            fontWeight: 700,
            color: COLORS.textPrimary,
          }}
        >
          Knowledge{" "}
          <span style={{ color: COLORS.accentTeal }}>Graph</span>
        </div>
        <div
          style={{
            fontSize: 18,
            fontFamily: FONTS.body,
            color: COLORS.textSecondary,
            marginTop: 8,
          }}
        >
          Entity extraction · Relationship mapping · Graph-augmented retrieval
        </div>
      </div>

      {/* Edges (rendered behind nodes) */}
      <svg
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          overflow: "visible",
        }}
      >
        {EDGES.map((edge, i) => {
          const fromNode = nodeMap.get(edge.from);
          const toNode = nodeMap.get(edge.to);
          if (!fromNode || !toNode) return null;

          const edgeAppear = spring({
            frame: frame - edge.delay,
            fps,
            config: SPRING.gentle,
          });

          const progress = interpolate(edgeAppear, [0, 1], [0, 1]);
          const opacity = interpolate(edgeAppear, [0, 0.3], [0, 0.6], {
            extrapolateRight: "clamp",
          });

          const endX = fromNode.x + (toNode.x - fromNode.x) * progress;
          const endY = fromNode.y + (toNode.y - fromNode.y) * progress;

          // Edge label midpoint
          const midX = (fromNode.x + toNode.x) / 2;
          const midY = (fromNode.y + toNode.y) / 2;

          return (
            <React.Fragment key={i}>
              <line
                x1={fromNode.x}
                y1={fromNode.y}
                x2={endX}
                y2={endY}
                stroke={fromNode.color}
                strokeWidth={1.5}
                opacity={opacity}
                strokeDasharray="4 4"
              />
              {/* Edge label */}
              {progress > 0.8 && (
                <text
                  x={midX}
                  y={midY - 8}
                  textAnchor="middle"
                  fill={COLORS.textMuted}
                  fontSize={10}
                  fontFamily={FONTS.mono}
                  opacity={interpolate(progress, [0.8, 1], [0, 0.7])}
                >
                  {edge.label}
                </text>
              )}
            </React.Fragment>
          );
        })}
      </svg>

      {/* Nodes */}
      {NODES.map((node, i) => {
        const nodeAppear = spring({
          frame: frame - node.delay,
          fps,
          config: SPRING.bouncy,
        });

        const scale = interpolate(nodeAppear, [0, 1], [0, 1]);
        const opacity = interpolate(nodeAppear, [0, 1], [0, 1]);

        // Subtle float
        const floatY = Math.sin((frame + i * 15) * 0.03) * 4;

        return (
          <div
            key={i}
            style={{
              position: "absolute",
              left: node.x,
              top: node.y + floatY,
              transform: `translate(-50%, -50%) scale(${scale})`,
              opacity,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 6,
            }}
          >
            {/* Node circle */}
            <div
              style={{
                width: NODE_RADIUS * 2,
                height: NODE_RADIUS * 2,
                borderRadius: "50%",
                background: `radial-gradient(circle at 40% 35%, ${node.color}30, ${node.color}10)`,
                border: `2px solid ${node.color}60`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: `0 0 20px ${node.color}20`,
              }}
            >
              <span
                style={{
                  fontFamily: FONTS.mono,
                  fontSize: 11,
                  color: node.color,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                }}
              >
                {node.type}
              </span>
            </div>
            {/* Node label */}
            <span
              style={{
                fontFamily: FONTS.mono,
                fontSize: 13,
                color: COLORS.textPrimary,
                fontWeight: 500,
              }}
            >
              {node.label}
            </span>
          </div>
        );
      })}

      {/* Legend */}
      {(() => {
        const legendAppear = spring({
          frame: frame - 100,
          fps,
          config: SPRING.snappy,
        });
        const types = [
          { type: "PROJECT", color: COLORS.accentBlue },
          { type: "TOOL", color: COLORS.accentTeal },
          { type: "CONCEPT", color: COLORS.accentPurple },
        ];
        return (
          <div
            style={{
              position: "absolute",
              bottom: 60,
              left: "50%",
              transform: "translate(-50%, 0)",
              display: "flex",
              gap: 30,
              opacity: interpolate(legendAppear, [0, 1], [0, 1]),
            }}
          >
            {types.map((t, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div
                  style={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    background: t.color,
                    boxShadow: `0 0 8px ${t.color}60`,
                  }}
                />
                <span
                  style={{
                    fontFamily: FONTS.mono,
                    fontSize: 13,
                    color: COLORS.textMuted,
                  }}
                >
                  {t.type}
                </span>
              </div>
            ))}
          </div>
        );
      })()}
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
